/**
 * Residents API — all backend calls for the Residents tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// Re-export so the Residents page can fetch wandering devices from the
// same API surface as the other residents-related calls.
export { fetchWanderingDevices } from '../../../devices/api/devicesApi.js';

/** Generates UTC timestamp in "YYYY-MM-DD HH:MM:SS" format matching V1's getUTCTimestamp */
function formatUTCTimestamp() {
  const d = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all residents (with devices) for a campus.
 * V1 procedure: sp_dev_getallresidentsanddevicesbycampusid
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true] — false on first load to bust cache
 * @returns {Promise<Array>}
 */
export function fetchResidents(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getallresidentsanddevicesbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch all areas for a campus.
 * V1 procedure: sp_dev_getareasbycampusid
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch available devices for assignment (Inovonics devices).
 * V1 procedure: sp_dev_getInovonicsDevices
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchDevices(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: '1,2,3,6,7,8,9,10,11,12,15,16,17',
      campus_id: campusId,
    },
    readOnly: true,
  });
}

/**
 * Fetch simple group list (id + name) for the resident form group selector.
 * V1 table: core_resident_groups (fetchbyparameter)
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchGroupList(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_resident_groups',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new resident.
 * V1 TransType: resident_add
 *
 * @param {object} data — Resident form data
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addResident(data, session) {
  const payload = [{
    campus_id: session.campusId,
    resident_first_name: data.resident_first_name,
    resident_last_name: data.resident_last_name,
    resident_nickname: data.resident_nickname || '',
    resident_phone: (data.resident_phone || '').replaceAll('-', ''),
    resident_email: (data.resident_email || '').toLowerCase(),
    is_area_assigned: true,
    area_id: data.area_id,
    resident_group_ids: data.resident_group_ids || [],
    move_in_date: data.move_in_date || formatUTCTimestamp(),
    ehr_external_id: data.ehr_external_id || '',
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  return apiTransaction({
    transType: 'resident_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing resident.
 * V1 TransType: resident_edit
 *
 * @param {object} data — Updated resident data
 * @param {object} oldData — Original resident data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editResident(data, oldData, session) {
  const payload = [{
    resident_id: parseInt(data.resident_id),
    resident_first_name: data.resident_first_name,
    resident_last_name: data.resident_last_name,
    resident_nickname: data.resident_nickname || '',
    is_active: 1,
    resident_email: (data.resident_email || '').toLowerCase(),
    resident_phone: (data.resident_phone || '').replaceAll('-', ''),
    area_id: parseInt(data.area_id),
    resident_group_ids: data.resident_group_ids || [],
    campus_id: session.campusId,
    ehr_external_id: data.ehr_external_id || '',
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    resident_id: parseInt(oldData.resident_id),
    resident_first_name: oldData.resident_first_name,
    resident_last_name: oldData.resident_last_name,
    resident_nickname: oldData.resident_nickname || '',
    is_active: oldData.is_active ? 1 : 0,
    resident_email: oldData.resident_email || '',
    resident_phone: oldData.resident_phone || '',
    area_id: oldData.area_id,
    resident_group_ids: oldData.resident_group_ids || [],
    campus_id: oldData.campus_id || session.campusId,
    ehr_external_id: oldData.ehr_external_id || '',
  }];

  return apiTransaction({
    transType: 'resident_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Move out (disable) one or more residents.
 * V1 TransType: resident_disable
 *
 * @param {Array<number>} residentIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function moveOutResidents(residentIds, session) {
  const payload = residentIds.map((id) => ({
    resident_id: id,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }));

  return apiTransaction({
    transType: 'resident_disable',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Permanently delete one or more residents.
 * V1 TransType: resident_delete
 *
 * @param {Array<number>} residentIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteResidents(residentIds, session) {
  const payload = residentIds.map((id) => ({
    resident_id: parseInt(id),
  }));

  return apiTransaction({
    transType: 'resident_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// DEVICE operations
// ---------------------------------------------------------------------------

/**
 * Assign a device to a resident.
 * V1 TransType: device_assign_resident (Residents.jsx lines 1498-1535)
 *
 * @param {object} device — Full device record from fetchDevices (sp_dev_getInovonicsDevices)
 * @param {number} residentId — The resident to assign to
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @returns {Promise<[boolean, string?]>}
 */
export function assignDevice(device, residentId, session, residentAreaId) {
  const payload = [{
    device_id: device.device_id,
    campus_id: session.campusId,
    is_active: true,
    resident_id: parseInt(residentId),
    device_name: device.device_name,
    manufacture_model_id: parseInt(device.manufacture_model_id),
    manufacture_id: device.manufacture_id,
    resident_area_id: residentAreaId || 0,
    device_use_id: parseInt(device.device_use_id),
    descriptions: device.descriptions || '',
    is_resident: true,
    is_area_device: false,
    area_id: 0,
    device_type_id: device.device_type_id,
    modified_by: session.userId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    campus_id: device.campus_id,
    is_active: device.is_active,
    resident_id: device.resident_id,
    device_name: device.device_name,
    manufacture_model_id: device.manufacture_model_id,
    device_use_id: device.device_use_id,
    descriptions: device.descriptions,
    is_resident: device.is_resident,
    is_area_device: device.is_area_device,
    area_id: device.area_id,
    device_type_id: device.device_type_id,
  }];

  return apiTransaction({
    transType: 'device_assign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign (remove) a device from a resident.
 * V1 TransType: device_unassign_resident (Residents.jsx lines 997-1019)
 *
 * @param {number} deviceId — The device to unassign
 * @param {number} residentId — The resident it's currently assigned to
 * @param {object} session — { campusId, userId, clientId, offsite, offsiteId }
 * @param {string|null} deviceName — Device name (used by Inovonics provisioning audit)
 * @returns {Promise<[boolean, string?]>}
 */
export function unassignDevice(deviceId, residentId, session, deviceName = null) {
  const payload = [{
    device_id: deviceId,
    device_name: deviceName,
    is_active: false,
    resident_id: null,
    modified_by: session.userId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    device_id: deviceId,
    resident_id: residentId,
  }];

  return apiTransaction({
    transType: 'device_unassign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
